import { createAsyncThunk } from '@reduxjs/toolkit';
import { errMsg } from '../utils/errMsg';
import {
    setLikedMixes,
    appendLikedMixes,
    enrichLikedMixes,
    addMixLike,
    removeMixLike,
    updateLikedMixSet,
    setMixLoading,
    setMixError,
} from '../slices/likedMixesSlice';

import { refreshAndRetry } from '../utils/refreshAndRetry';
import { ensureToken } from './djpalThunk';
import { asCatalogId } from './likesThunk';
import { entityId } from '../../utils/entityId';
import { track } from '../../utils/analytics';

export const fetchLikedMixes = createAsyncThunk(
    'likedMixes/fetchLikedMixes',
    async ({ page = 1, limit = 50, append = false } = {}, { dispatch, getState }) => {
        try {
            dispatch(setMixLoading(true));

            const result = await refreshAndRetry(
                (token) => window.electron.library.getLikedMixes(token, page, limit)
            );

            if (result.error) {
                dispatch(setMixError(errMsg(result.error)));
                return;
            }

            // Normalize at the ingestion boundary (entityId.js convention):
            // a user_likes row's `id` is the LIKE-ROW integer, not the mix —
            // left as-is it poisons entityId()/navigation. Expose the catalog
            // public id (track_public_id holds it for type='mix') as `.id`,
            // keep the like-row id under `likeId`.
            const data = (result.data || []).map(r => ({
                ...r,
                likeId: r.id,
                id: r.track_public_id || null,
            }));
            const total = result.pagination?.total || 0;

            if (append) {
                dispatch(appendLikedMixes({ data, total }));
            } else {
                dispatch(setLikedMixes({ data, total }));
            }

            // Liked-mix snapshots store no publicId and no native cover. Enrich
            // them (by link, one batched call) so they open by id (fast) and
            // render the native cover (DJ artwork → first track cover). Best
            // effort — the list already rendered above.
            try {
                const links = [...new Set(data.map(m => m && m.link).filter(Boolean))];
                if (links.length && window.electron?.djpal?.resolveMixesByLinks) {
                    const token = await ensureToken(dispatch, getState);
                    const resp = await window.electron.djpal.resolveMixesByLinks(token, links);
                    const arr = resp?.data || (Array.isArray(resp) ? resp : []);
                    if (arr.length) dispatch(enrichLikedMixes(arr));
                }
            } catch { /* enrichment is best-effort */ }

            const setUpdate = {};
            for (const mix of data) {
                setUpdate[`${mix.title}|||${mix.dj_name}`] = true;
            }
            dispatch(updateLikedMixSet(setUpdate));
        } catch (error) {
            console.error('Error fetching liked mixes:', error);
            dispatch(setMixError(error.message));
        } finally {
            dispatch(setMixLoading(false));
        }
    }
);

export const likeMix = createAsyncThunk(
    'likedMixes/likeMix',
    async (mix, { dispatch }) => {
        try {
            const djName = mix.djName || mix.artist;
            const coverFromTrack = mix.coverFromTrack || mix.cover_from_track || null;
            // Same guard as track likes (see likesThunk asCatalogId): a foreign
            // integer in `.id` must never be persisted as the catalog id —
            // that's the 2026-08-06 poisoned-likes incident, mix flavor.
            const mixCatalogId = asCatalogId(entityId(mix));
            // Optimistic update — carry the catalog id so the fresh row
            // navigates/plays by id like server-fetched rows do.
            dispatch(addMixLike({
                id: mixCatalogId,
                title: mix.title,
                dj_name: djName,
                artwork: mix.artwork || mix.artworkUrl || mix.image || null,
                cover_from_track: coverFromTrack,
                link: mix.link || null,
                date: mix.date || null,
                created_at: new Date().toISOString(),
            }));

            const result = await refreshAndRetry(
                (token) => window.electron.library.likeMix(token, {
                    // music-info-server id of the mix (stored as the like's entity id)
                    id: mixCatalogId,
                    artist: djName,
                    title: mix.title,
                    artwork: mix.artwork || mix.artworkUrl || mix.image || null,
                    coverFromTrack,
                    link: mix.link || null,
                    date: mix.date || null,
                    djName: djName,
                })
            );

            if (result.error) {
                dispatch(removeMixLike({ title: mix.title, djName }));
                console.error('Mix like failed:', result.error);
            } else {
                track('mix_liked', { mix_id: mixCatalogId || undefined, artist: djName });
            }
        } catch (error) {
            const djName = mix.djName || mix.artist;
            dispatch(removeMixLike({ title: mix.title, djName }));
            console.error('Error liking mix:', error);
        }
    }
);

export const unlikeMix = createAsyncThunk(
    'likedMixes/unlikeMix',
    async ({ title, djName }, { dispatch }) => {
        try {
            // Optimistic update
            dispatch(removeMixLike({ title, djName }));

            const result = await refreshAndRetry(
                (token) => window.electron.library.unlikeMix(token, title, djName)
            );

            if (result.error) {
                console.error('Mix unlike failed:', result.error);
            } else {
                track('mix_unliked', { artist: djName });
            }
        } catch (error) {
            console.error('Error unliking mix:', error);
        }
    }
);

export const checkMixLikes = createAsyncThunk(
    'likedMixes/checkMixLikes',
    async (mixes, { dispatch }) => {
        try {
            const tracksPayload = mixes.map(m => ({
                artist: m.djName || m.artist,
                title: m.title,
            }));

            const result = await refreshAndRetry(
                (token) => window.electron.library.checkMixLikes(token, tracksPayload)
            );

            if (result.error) {
                console.error('Check mix likes failed:', result.error);
                return;
            }

            const data = result.data || [];
            const setUpdate = {};
            mixes.forEach((mix, i) => {
                const djName = mix.djName || mix.artist;
                setUpdate[`${mix.title}|||${djName}`] = data[i] || false;
            });
            dispatch(updateLikedMixSet(setUpdate));
        } catch (error) {
            console.error('Error checking mix likes:', error);
        }
    }
);
